// Invite Members card shown after "Invite members" is clicked on the Team Members page.
// Fields follow the old Loyalty Cloud "Add a New Team Member" modal: First Name, Last Name,
// Email, Role, and Property Permissions (chips + a "pick a property / Add" row), restyled in
// the new UI (Figma node 18970:2310 card chrome). Super Admin and multi-account rows were
// dropped: the backend `POST /api/team_member/` creates one user for one account and has no
// field for `master`.
//
// Payload mirrors the old app's request exactly (multipart form): first_name, last_name,
// email, role, permissions (comma-separated property sfids), company_name, added_by (the
// inviting user's EMAIL), associated_sfid (current account), loyalty_cloud_account_id (sent
// empty, as the old app does) and both notification flags "false".
// `onInvite({sent, failed})` is called after the member is created.
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, ButtonBase, Stack, Tooltip, Typography} from '@mui/material';

import {tokens} from '../../themes/ThemeCustomization';
import {getEmail} from '../../app/store/reducers/session';
import {useLazyGetCommunitiesByAccountSfidQuery} from '../../features/coreApi/communitiesSlice';
import {useAddTeamMemberMutation} from '../../features/loyaltyCloudApi/apiSlice';
import chevronIcon from '../../assets/shell/pagination-dropdown-icon.svg';

const ROLES = ['Administrator', 'Regional Property Manager', 'Property Manager', 'Leasing Agent'];
const ADMIN_ROLE = 'Administrator';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MEMBER_TOOLTIP = "Enter the new team member's name, email address and role.";
const PERMISSIONS_TOOLTIP = 'Choose which properties this team member can access. Administrators can access every property.';

const errorMessage = (error, email) => {
    if (error?.status === 409) return `${email} already has an account.`;
    if (error?.status === 403) return `You don't have permission to invite ${email}.`;
    const detail = typeof error?.data === 'string' ? error.data : error?.data?.error || error?.data?.message;
    return `Couldn't invite ${email}${detail ? ` — ${detail}` : '.'}`;
};

const InfoIcon = ({title}) => (
    <Tooltip title={title} arrow placement="top">
        <Box component="svg" viewBox="0 0 16 16" aria-label="More info" sx={{width: 14, height: 14, color: tokens.color.neutral600, cursor: 'help'}}>
            <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <path d="M8 7.2v3.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <circle cx="8" cy="5.2" r="0.8" fill="currentColor" />
        </Box>
    </Tooltip>
);

const SectionHeader = ({children, tooltip}) => (
    <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{px: 2, height: 45, backgroundColor: tokens.color.neutral50, borderTop: `1px solid ${tokens.color.neutral200}`, borderBottom: `1px solid ${tokens.color.neutral200}`}}
    >
        <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral600}}>
            {children}
        </Typography>
        <InfoIcon title={tooltip} />
    </Stack>
);

const fieldSx = (disabled) => ({
    width: '100%',
    height: 36,
    px: 1.5,
    border: `1px solid ${tokens.color.neutral200}`,
    borderRadius: '6px',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: tokens.color.neutral950,
    backgroundColor: disabled ? tokens.color.neutral100 : tokens.color.white,
    cursor: disabled ? 'not-allowed' : undefined,
    outline: 'none',
    '&:focus': {borderColor: tokens.color.sky600},
    '&::placeholder': {color: tokens.color.neutral600, opacity: 0.7}
});

const TextField = ({label, value, onChange, type = 'text', placeholder, error}) => (
    <Stack spacing={1} sx={{flex: 1, minWidth: 0}}>
        <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral950}}>
            {label}
        </Typography>
        <Box
            component="input"
            type={type}
            value={value}
            placeholder={placeholder}
            aria-label={label}
            aria-invalid={Boolean(error)}
            onChange={(e) => onChange(e.target.value)}
            sx={{...fieldSx(false), ...(error ? {borderColor: '#e7000b'} : {})}}
        />
    </Stack>
);

const SelectField = ({label, value, onChange, disabled, placeholder, options}) => (
    <Stack spacing={1} sx={{flex: 1, minWidth: 0}}>
        {label && (
            <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral950}}>
                {label}
            </Typography>
        )}
        <Box sx={{position: 'relative'}}>
            <Box
                component="select"
                aria-label={label || placeholder}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                sx={{...fieldSx(disabled), pr: 4, appearance: 'none', color: value ? tokens.color.neutral950 : tokens.color.neutral600, cursor: disabled ? 'not-allowed' : 'pointer'}}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </Box>
            <Box component="img" src={chevronIcon} alt="" sx={{position: 'absolute', right: 12, top: 11, width: 14, height: 14, pointerEvents: 'none'}} />
        </Box>
    </Stack>
);

const InviteMembersForm = ({accountSfid, accountName, onCancel, onInvite}) => {
    const invitedBy = useSelector(getEmail);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('');
    const [communities, setCommunities] = useState([]);
    const [selected, setSelected] = useState([]); // property sfids already added as chips
    const [pending, setPending] = useState(''); // value of the "add a property" dropdown
    const [submitError, setSubmitError] = useState('');

    const [fetchCommunities] = useLazyGetCommunitiesByAccountSfidQuery();
    const [addTeamMember, {isLoading}] = useAddTeamMemberMutation();

    useEffect(() => {
        if (!accountSfid) return;
        fetchCommunities(accountSfid, true)
            .unwrap()
            .then(setCommunities)
            .catch(() => setCommunities([]));
    }, [accountSfid, fetchCommunities]);

    const isAdministrator = role === ADMIN_ROLE;
    const available = communities.filter((community) => !selected.includes(community.sfid));
    const emailInvalid = email.trim() !== '' && !EMAIL_PATTERN.test(email.trim());
    const canInvite =
        firstName.trim() !== '' &&
        lastName.trim() !== '' &&
        EMAIL_PATTERN.test(email.trim()) &&
        role !== '' &&
        (isAdministrator || selected.length > 0) &&
        Boolean(accountSfid) &&
        !isLoading;

    const addProperty = () => {
        if (!pending) return;
        setSelected((prev) => [...prev, pending]);
        setPending('');
    };

    const handleInvite = async () => {
        if (!canInvite) return;
        setSubmitError('');

        const trimmedEmail = email.trim();
        // Administrators get every property of the account.
        const permissions = isAdministrator ? communities.map((community) => community.sfid) : selected;

        const data = new FormData();
        data.append('first_name', firstName.trim());
        data.append('last_name', lastName.trim());
        data.append('email', trimmedEmail);
        data.append('role', role);
        data.append('permissions', permissions.join(','));
        data.append('company_name', accountName || '');
        data.append('added_by', invitedBy || '');
        data.append('associated_sfid', accountSfid);
        data.append('loyalty_cloud_account_id', '');
        data.append('notifications_campaign_updates_email', 'false');
        data.append('notifications_campaign_updates_text', 'false');

        try {
            await addTeamMember(data).unwrap();
            onInvite?.({sent: [trimmedEmail], failed: []});
        } catch (error) {
            setSubmitError(errorMessage(error, trimmedEmail));
        }
    };

    return (
        <Box sx={{backgroundColor: tokens.color.white, border: `1px solid ${tokens.color.neutral200}`, borderRadius: '8px', overflow: 'hidden'}}>
            <Box sx={{p: 2, pb: 1.75}}>
                <Typography sx={{fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: tokens.color.neutral950}}>Invite Members</Typography>
            </Box>

            <SectionHeader tooltip={MEMBER_TOOLTIP}>Team member details</SectionHeader>
            <Box sx={{p: 2}}>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <TextField label="First Name" value={firstName} onChange={setFirstName} />
                    <TextField label="Last Name" value={lastName} onChange={setLastName} />
                    <TextField label="Email" type="email" value={email} onChange={setEmail} placeholder="name@example.com" error={emailInvalid} />
                    <SelectField
                        label="Role"
                        value={role}
                        placeholder="Select a role"
                        options={ROLES.map((value) => ({value, label: value}))}
                        onChange={setRole}
                    />
                </Stack>
                {emailInvalid && (
                    <Typography variant="caption" sx={{color: '#e7000b', display: 'block', mt: 0.5}}>
                        Enter a valid email address.
                    </Typography>
                )}
            </Box>

            <SectionHeader tooltip={PERMISSIONS_TOOLTIP}>Property permissions</SectionHeader>
            <Stack spacing={2} sx={{p: 2}}>
                {isAdministrator ? (
                    <Typography variant="body2" sx={{color: tokens.color.neutral600}}>
                        Administrators have access to all properties.
                    </Typography>
                ) : (
                    <>
                        {selected.length > 0 && (
                            <Stack direction="row" flexWrap="wrap" sx={{gap: 1}}>
                                {selected.map((sfid) => (
                                    <Stack
                                        key={sfid}
                                        direction="row"
                                        alignItems="center"
                                        sx={{gap: 0.75, height: 28, pl: 1.5, pr: 1, borderRadius: '999px', border: `1px solid ${tokens.color.sky600}`, color: tokens.color.sky600}}
                                    >
                                        <Typography variant="body2" sx={{color: 'inherit'}}>
                                            {communities.find((community) => community.sfid === sfid)?.name || sfid}
                                        </Typography>
                                        <ButtonBase
                                            aria-label="Remove property"
                                            onClick={() => setSelected((prev) => prev.filter((value) => value !== sfid))}
                                            sx={{width: 14, height: 14, color: 'inherit'}}
                                        >
                                            <Box component="svg" viewBox="0 0 10 10" sx={{width: 10, height: 10}}>
                                                <path d="M2 2l6 6M8 2L2 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                            </Box>
                                        </ButtonBase>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{flex: 1, maxWidth: 360}}>
                                <SelectField
                                    value={pending}
                                    disabled={available.length === 0}
                                    placeholder={available.length === 0 ? 'No more properties available' : 'Select a property'}
                                    options={available.map((community) => ({value: community.sfid, label: community.name}))}
                                    onChange={setPending}
                                />
                            </Box>
                            <ButtonBase
                                onClick={addProperty}
                                disabled={!pending}
                                sx={{
                                    height: 36,
                                    px: 2,
                                    borderRadius: '6px',
                                    border: `1px solid ${tokens.color.neutral200}`,
                                    backgroundColor: pending ? tokens.color.white : tokens.color.neutral100,
                                    opacity: pending ? 1 : 0.6
                                }}
                            >
                                <Typography variant="body2" fontWeight={500}>
                                    Add
                                </Typography>
                            </ButtonBase>
                        </Stack>
                    </>
                )}

                {submitError && (
                    <Typography variant="caption" role="alert" sx={{color: '#e7000b'}}>
                        {submitError}
                    </Typography>
                )}
            </Stack>

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{px: 1.5, py: 1.5, backgroundColor: tokens.color.neutral50, borderTop: `1px solid ${tokens.color.neutral200}`}}
            >
                <ButtonBase
                    onClick={onCancel}
                    sx={{height: 32, px: 1.5, borderRadius: '6px', border: `1px solid ${tokens.color.neutral200}`, backgroundColor: tokens.color.white}}
                >
                    <Typography variant="body2" fontWeight={500}>
                        Cancel
                    </Typography>
                </ButtonBase>
                <ButtonBase
                    onClick={handleInvite}
                    disabled={!canInvite}
                    sx={{
                        height: 32,
                        px: 1.5,
                        borderRadius: '6px',
                        backgroundColor: tokens.color.sky600,
                        color: tokens.color.white,
                        opacity: canInvite ? 1 : 0.6,
                        '&:hover': {opacity: canInvite ? 0.9 : 0.6}
                    }}
                >
                    <Typography variant="body2" fontWeight={500} sx={{color: 'inherit'}}>
                        {isLoading ? 'Inviting…' : 'Invite'}
                    </Typography>
                </ButtonBase>
            </Stack>
        </Box>
    );
};

export default InviteMembersForm;
